import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { toast } from "react-toastify";

import CompanyAccountsExcel from "./CompanyAccountsExcel";

const TYPES = [
  "ACTIVO",
  "PASIVO",
  "PATRIMONIO",
  "INGRESO",
  "GASTO",
  "COSTO",
];

const BALANCES = {
  ACTIVO: "DEBITO",
  PASIVO: "CREDITO",
  PATRIMONIO: "CREDITO",
  INGRESO: "CREDITO",
  GASTO: "DEBITO",
  COSTO: "DEBITO",
};

const emptyForm = () => ({
  code: "",
  name: "",
  description: "",
  parent_id: "",
  account_type: "ACTIVO",
  normal_balance: "DEBITO",
  allows_posting: true,
  requires_cost_center: false,
  is_active: true,
});

function errorMessage(error) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  if (error?.response?.status === 403) {
    return "No tienes permiso para realizar esta operación.";
  }

  if (!error?.response && error?.message) {
    return error.message;
  }

  return "No fue posible completar la operación.";
}

export default function CompanyAccounts({
  apiUrl,
  onChanged,
}) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const submitLock = useRef(false);

  const baseUrl = apiUrl.replace(/\/+$/, "");

  const requestConfig = useCallback(() => {
    const token = localStorage.getItem("company_token");

    if (!token) {
      throw new Error(
        "La sesión de empresa no está disponible."
      );
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const response = await axios.get(
        `${baseUrl}/accounting/accounts`,
        requestConfig()
      );

      if (!Array.isArray(response.data)) {
        throw new Error(
          "La respuesta del listado de cuentas no es válida."
        );
      }

      setAccounts(response.data);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [baseUrl, requestConfig]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const term = search
      .trim()
      .toLocaleLowerCase("es");

    return accounts.filter((account) =>
      `${account.code} ${account.name}`
        .toLocaleLowerCase("es")
        .includes(term)
    );
  }, [accounts, search]);

  // Paginación del listado filtrado.
  const totalRecords = filteredAccounts.length;

  const totalPages = Math.max(
    1,
    Math.ceil(totalRecords / pageSize)
  );

  const safePage = Math.min(currentPage, totalPages);

  const startIndex = (safePage - 1) * pageSize;

  const paginatedAccounts = filteredAccounts.slice(
    startIndex,
    startIndex + pageSize
  );

  const firstRecord =
    totalRecords === 0 ? 0 : startIndex + 1;

  const lastRecord = Math.min(
    startIndex + pageSize,
    totalRecords
  );

  const visiblePages = useMemo(() => {
    const pages = new Set([
      1,
      totalPages,
      safePage - 1,
      safePage,
      safePage + 1,
    ]);

    const sorted = [...pages]
      .filter(
        (page) => page >= 1 && page <= totalPages
      )
      .sort((first, second) => first - second);

    const result = [];

    sorted.forEach((page, index) => {
      if (index > 0) {
        const previous = sorted[index - 1];

        if (page - previous === 2) {
          result.push(previous + 1);
        } else if (page - previous > 2) {
          result.push(
            `ellipsis-${previous}-${page}`
          );
        }
      }

      result.push(page);
    });

    return result;
  }, [safePage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize]);

  useEffect(() => {
    setCurrentPage((page) =>
      Math.min(page, totalPages)
    );
  }, [totalPages]);

  const accountNames = useMemo(
    () =>
      new Map(
        accounts.map((account) => [
          Number(account.id),
          `${account.code} — ${account.name}`,
        ])
      ),
    [accounts]
  );

  // Excluye la cuenta editada y sus descendientes.
  const excludedParents = useMemo(() => {
    const excluded = new Set();

    if (editingId === null) {
      return excluded;
    }

    const pending = [Number(editingId)];

    while (pending.length > 0) {
      const id = pending.pop();

      if (excluded.has(id)) {
        continue;
      }

      excluded.add(id);

      accounts.forEach((account) => {
        if (
          account.parent_id != null &&
          Number(account.parent_id) === id
        ) {
          pending.push(Number(account.id));
        }
      });
    }

    return excluded;
  }, [accounts, editingId]);

  const parentOptions = useMemo(
    () =>
      accounts.filter((account) => {
        if (
          excludedParents.has(Number(account.id))
        ) {
          return false;
        }

        const isSelectedParent =
          form.parent_id !== "" &&
          Number(account.id) ===
            Number(form.parent_id);

        const isValidParent =
          account.is_active &&
          !account.allows_posting &&
          account.account_type ===
            form.account_type;

        return isSelectedParent || isValidParent;
      }),
    [
      accounts,
      excludedParents,
      form.parent_id,
      form.account_type,
    ]
  );

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    if (saving) {
      return;
    }

    setEditingId(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (account) => {
    if (saving) {
      return;
    }

    setEditingId(Number(account.id));

    setForm({
      code: account.code,
      name: account.name,
      description: account.description || "",
      parent_id:
        account.parent_id != null
          ? String(account.parent_id)
          : "",
      account_type: account.account_type,
      normal_balance: account.normal_balance,
      allows_posting: account.allows_posting,
      requires_cost_center:
        account.requires_cost_center,
      is_active: account.is_active,
    });

    setShowForm(true);
  };

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setForm((current) => {
      const updated = {
        ...current,
        [name]:
          type === "checkbox" ? checked : value,
      };

      if (name === "account_type") {
        updated.normal_balance = BALANCES[value];
        updated.parent_id = "";
      }

      return updated;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (submitLock.current) {
      return;
    }

    const code = form.code.trim();
    const name = form.name.trim();

    if (!code || name.length < 2) {
      toast.error(
        "Ingresa un código y un nombre válido."
      );
      return;
    }

    const duplicate = accounts.some(
      (account) =>
        (
          editingId === null ||
          Number(account.id) !== editingId
        ) &&
        account.code === code
    );

    if (duplicate) {
      toast.error(
        "Ya existe una cuenta con ese código."
      );
      return;
    }

    const parent = form.parent_id
      ? accounts.find(
          (account) =>
            Number(account.id) ===
            Number(form.parent_id)
        )
      : null;

    if (
      form.parent_id &&
      (
        !parent ||
        excludedParents.has(Number(parent.id)) ||
        !parent.is_active ||
        parent.allows_posting ||
        parent.account_type !== form.account_type
      )
    ) {
      toast.error(
        "Selecciona una cuenta superior activa, del mismo tipo y sin movimientos directos."
      );
      return;
    }

    if (
      editingId !== null &&
      form.allows_posting &&
      accounts.some(
        (account) =>
          account.parent_id != null &&
          Number(account.parent_id) === editingId
      )
    ) {
      toast.error(
        "Una cuenta con subcuentas debe permanecer como cuenta de agrupación."
      );
      return;
    }

    const payload = {
      ...form,
      code,
      name,
      description:
        form.description.trim() || null,
      parent_id: parent
        ? Number(parent.id)
        : null,
      level: parent
        ? Number(parent.level) + 1
        : 1,
    };

    submitLock.current = true;
    setSaving(true);

    try {
      const response =
        editingId === null
          ? await axios.post(
              `${baseUrl}/accounting/accounts`,
              payload,
              requestConfig()
            )
          : await axios.put(
              `${baseUrl}/accounting/accounts/${editingId}`,
              payload,
              requestConfig()
            );

      const saved = response.data;

      setAccounts((current) =>
        [
          ...current.filter(
            (account) =>
              Number(account.id) !==
              Number(saved.id)
          ),
          saved,
        ].sort((first, second) =>
          first.code.localeCompare(second.code)
        )
      );

      toast.success(
        editingId === null
          ? "Cuenta contable creada correctamente."
          : "Cuenta contable actualizada correctamente."
      );

      closeForm();

      try {
        await onChanged?.();
      } catch {
        toast.warning(
          "La cuenta se guardó. Actualiza los selectores del módulo."
        );
      }
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      submitLock.current = false;
      setSaving(false);
    }
  };

  const handleExcelImported = async () => {
    await loadAccounts();
    await onChanged?.();
  };

  return (
    <section className="accounting-card">
      <div className="accounting-card-heading">
        <div>
          <h2>Plan de cuentas</h2>

          <p>
            Administra las cuentas contables de
            esta empresa.
          </p>
        </div>

        <button
          type="button"
          className="btn-primary"
          onClick={openCreate}
          disabled={
            loading ||
            saving ||
            Boolean(loadError)
          }
        >
          Nueva cuenta
        </button>
      </div>

      <fieldset
        disabled={saving}
        className="accounting-account-fields"
      >
        <CompanyAccountsExcel
          apiUrl={baseUrl}
          onImported={handleExcelImported}
        />
      </fieldset>

      {showForm && (
        <form onSubmit={handleSubmit}>
          <h3>
            {editingId === null
              ? "Nueva cuenta"
              : "Editar cuenta"}
          </h3>

          <fieldset
            disabled={saving}
            className="accounting-account-fields"
          >
            <div className="accounting-account-grid">
              <label>
                Código

                <input
                  type="text"
                  name="code"
                  value={form.code}
                  onChange={handleChange}
                  maxLength={20}
                  required
                />
              </label>

              <label>
                Nombre

                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  minLength={2}
                  maxLength={200}
                  required
                />
              </label>

              <label>
                Tipo de cuenta

                <select
                  name="account_type"
                  value={form.account_type}
                  onChange={handleChange}
                >
                  {TYPES.map((type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Naturaleza

                <input
                  type="text"
                  value={form.normal_balance}
                  readOnly
                />
              </label>

              <label>
                Cuenta superior

                <select
                  name="parent_id"
                  value={form.parent_id}
                  onChange={handleChange}
                >
                  <option value="">
                    Sin cuenta superior
                  </option>

                  {parentOptions.map((account) => (
                    <option
                      key={account.id}
                      value={account.id}
                    >
                      {account.code} — {account.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Descripción

                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  maxLength={1000}
                  rows={2}
                />
              </label>
            </div>

            <div className="accounting-account-checks">
              <label>
                <input
                  type="checkbox"
                  name="allows_posting"
                  checked={form.allows_posting}
                  onChange={handleChange}
                />

                Permite movimientos directos
              </label>

              <label>
                <input
                  type="checkbox"
                  name="requires_cost_center"
                  checked={
                    form.requires_cost_center
                  }
                  onChange={handleChange}
                />

                Requiere centro de costo
              </label>

              <label>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />

                Cuenta activa
              </label>
            </div>

            <p>
              Desmarca “Permite movimientos directos”
              para crear una cuenta que agrupe
              subcuentas.
            </p>

            <div className="accounting-account-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving
                  ? "Guardando..."
                  : "Guardar cuenta"}
              </button>

              <button
                type="button"
                className="btn-secondary"
                onClick={closeForm}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </fieldset>
        </form>
      )}

      <div className="accounts-list">
        <div className="accounts-list-toolbar">
          <label className="accounts-list-search">
            <span>Buscar cuenta</span>

            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Código o nombre de la cuenta..."
            />
          </label>

          <label className="accounts-list-size">
            <span>Mostrar</span>

            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(
                  Number(event.target.value)
                );
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span>registros</span>
          </label>
        </div>

        {loadError ? (
          <div
            role="alert"
            className="accounting-alert"
          >
            <span>{loadError}</span>

            <button
              type="button"
              className="btn-secondary"
              onClick={loadAccounts}
              disabled={loading || saving}
            >
              Reintentar
            </button>
          </div>
        ) : loading ? (
          <p
            className="accounting-empty"
            role="status"
          >
            Cargando cuentas...
          </p>
        ) : (
          <>
            <div className="accounts-list-table-scroll">
              <table className="accounts-list-table">
                <caption className="accounts-list-sr-only">
                  Plan de cuentas contables de la
                  empresa
                </caption>

                <thead>
                  <tr>
                    <th scope="col">Código</th>
                    <th scope="col">
                      Nombre de la cuenta
                    </th>
                    <th scope="col">Tipo</th>
                    <th scope="col">
                      Cuenta superior
                    </th>
                    <th scope="col">Uso</th>
                    <th scope="col">Estado</th>
                    <th
                      scope="col"
                      className="accounts-list-actions"
                    >
                      Acciones
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedAccounts.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="accounts-list-empty">
                          <strong>
                            {search.trim()
                              ? "No encontramos coincidencias"
                              : "Todavía no hay cuentas"}
                          </strong>

                          <span>
                            {search.trim()
                              ? "Prueba con otro código o nombre."
                              : "Crea una cuenta o importa tu archivo Excel."}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedAccounts.map((account) => (
                      <tr key={account.id}>
                        <td>
                          <span className="accounts-list-code">
                            {account.code}
                          </span>
                        </td>

                        <td>
                          <span className="accounts-list-name">
                            {account.name}
                          </span>
                        </td>

                        <td>
                          <span className="accounts-list-type">
                            {account.account_type}
                          </span>
                        </td>

                        <td className="accounts-list-parent">
                          {account.parent_id != null
                            ? accountNames.get(
                                Number(account.parent_id)
                              ) || "—"
                            : "—"}
                        </td>

                        <td>
                          <span
                            className={`accounts-list-badge ${
                              account.allows_posting
                                ? "accounts-list-badge-posting"
                                : "accounts-list-badge-group"
                            }`}
                          >
                            {account.allows_posting
                              ? "Movimientos"
                              : "Agrupación"}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`accounts-list-status ${
                              account.is_active
                                ? "is-active"
                                : "is-inactive"
                            }`}
                          >
                            <span aria-hidden="true" />

                            {account.is_active
                              ? "Activa"
                              : "Inactiva"}
                          </span>
                        </td>

                        <td className="accounts-list-actions">
                          <button
                            type="button"
                            className="accounts-list-edit"
                            onClick={() =>
                              openEdit(account)
                            }
                            disabled={saving}
                            aria-label={`Editar cuenta ${account.code} ${account.name}`}
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="accounts-list-footer">
              <p
                className="accounts-list-summary"
                aria-live="polite"
                aria-atomic="true"
              >
                Mostrando{" "}
                <strong>{firstRecord}</strong> a{" "}
                <strong>{lastRecord}</strong> de{" "}
                <strong>{totalRecords}</strong>{" "}
                registros

                {search.trim() && (
                  <span>
                    {" "}· {accounts.length} en total
                  </span>
                )}
              </p>

              <nav
                className="accounts-list-pagination"
                aria-label="Paginación del plan de cuentas"
              >
                <button
                  type="button"
                  className="accounts-list-page"
                  onClick={() =>
                    setCurrentPage(1)
                  }
                  disabled={safePage === 1}
                  aria-label="Primera página"
                  title="Primera página"
                >
                  «
                </button>

                <button
                  type="button"
                  className="accounts-list-page accounts-list-page-direction"
                  onClick={() =>
                    setCurrentPage(
                      Math.max(1, safePage - 1)
                    )
                  }
                  disabled={safePage === 1}
                  aria-label="Página anterior"
                >
                  ‹ <span>Anterior</span>
                </button>

                {visiblePages.map((page) =>
                  typeof page === "number" ? (
                    <button
                      key={page}
                      type="button"
                      className={`accounts-list-page ${
                        safePage === page
                          ? "is-current"
                          : ""
                      }`}
                      onClick={() =>
                        setCurrentPage(page)
                      }
                      aria-label={`Página ${page}`}
                      aria-current={
                        safePage === page
                          ? "page"
                          : undefined
                      }
                    >
                      {page}
                    </button>
                  ) : (
                    <span
                      key={page}
                      className="accounts-list-ellipsis"
                      aria-hidden="true"
                    >
                      …
                    </span>
                  )
                )}

                <button
                  type="button"
                  className="accounts-list-page accounts-list-page-direction"
                  onClick={() =>
                    setCurrentPage(
                      Math.min(
                        totalPages,
                        safePage + 1
                      )
                    )
                  }
                  disabled={
                    safePage === totalPages
                  }
                  aria-label="Página siguiente"
                >
                  <span>Siguiente</span> ›
                </button>

                <button
                  type="button"
                  className="accounts-list-page"
                  onClick={() =>
                    setCurrentPage(totalPages)
                  }
                  disabled={
                    safePage === totalPages
                  }
                  aria-label="Última página"
                  title="Última página"
                >
                  »
                </button>
              </nav>
            </div>
          </>
        )}
      </div>
    </section>
  );
}